import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import appInsightsService from '@/helpers/AppInsights';
import { isPostHogStarted, startPostHog } from '@/helpers/posthogClient';
import { CookieConsent, onConsentChanged, readConsent, writeConsent } from '@/helpers/cookieConsent';
import {
  isAnalyticsRunning,
  markAnalyticsRunning,
  reloadIfAnalyticsRunning,
  setGoogleConsentState,
  startSweeping,
  stopSweeping,
} from '@/helpers/analyticsGate';
import { resolveInfoGaMeasurementId } from '@/helpers/gaHostPolicy';
import ConsentContext, { ConsentContextValue } from './ConsentContext';

/**
 * שער ההסכמה של האזור האישי (HAT-1081): מחזיק את בחירת העוגיות של המשתמש
 * ומיישר אליה את שלושת כלי המדידה - PostHog, Google Analytics ו-Application
 * Insights. אף כלי לא נטען לפני הסכמה; חזרה מהסכמה באמצע ביקור טוענת את העמוד
 * מחדש, כי סקריפט שכבר רץ אי אפשר לפרוק.
 *
 * יושב בתוך AuthProvider ו-PostHogProvider: הוא צריך את המשתמש הנוכחי כדי
 * לזהות אותו מחדש ברגע מתן ההסכמה (קריאות identify שנעשו לפניה נבלעות).
 */
const ConsentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());
  const [isReopened, setIsReopened] = useState(false);
  const { currentUser } = useAuthContext();
  const { initialize: initializeGA, identifyUser: identifyGAUser } = useGoogleAnalytics();
  const { identifyUser: identifyPostHogUser } = usePostHogIdentification();

  useEffect(() => onConsentChanged(setConsent), []);

  useEffect(() => {
    const granted = consent?.analytics === true;
    setGoogleConsentState(granted);

    if (!granted) {
      // גם "טרם הוחלט" מגיע לכאן: הכלים כבויים והמכשיר מנוקה באופן שוטף.
      startSweeping();
      reloadIfAnalyticsRunning();
      return;
    }

    stopSweeping();

    appInsightsService.start();
    // PostHog מאותחל כאן ולא בעליית האפליקציה - עצם האתחול פונה לשרתי PostHog
    // ומוסר את כתובת ה-IP, ולכן הוא חייב לחכות להסכמה.
    startPostHog();

    // SPR-1206 C: only a dedicated info measurement id may load here; www's G-ELLRDSWMY7 is blocked.
    const gaMeasurementId = resolveInfoGaMeasurementId(
      import.meta.env.VITE_PUBLIC_GOOGLE_ANALYTICS_ID,
      typeof window !== 'undefined' ? window.location.hostname : '',
    );
    if (gaMeasurementId) {
      initializeGA(gaMeasurementId);
    }

    // רק אם כלי כלשהו באמת עלה: הדגל קובע אם חזרה מהסכמה תטען את העמוד מחדש,
    // ואין טעם לטעון מחדש ב-tenant שאין בו מפתחות מוגדרים.
    if (appInsightsService.isStarted() || isPostHogStarted() || gaMeasurementId) {
      markAnalyticsRunning();
    }

    // מי שכבר מחובר מזוהה עכשיו - קריאות identify שרצו לפני ההסכמה לא נקלטו.
    if (currentUser) {
      void identifyPostHogUser(currentUser);
      void identifyGAUser(currentUser);
    }
    // הפונקציות מה-hooks נוצרות מחדש בכל רנדר; התלות המהותית היא הבחירה
    // והמשתמש, וכל הפעולות כאן אידמפוטנטיות.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent, currentUser]);

  const decide = useCallback((analytics: boolean) => {
    setConsent(writeConsent(analytics));
    setIsReopened(false);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      isNoticeOpen: consent === null || isReopened,
      openNotice: () => setIsReopened(true),
      closeNotice: () => setIsReopened(false),
      decide,
      isAnalyticsRunning,
    }),
    [consent, isReopened, decide]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export default ConsentProvider;
